import React, { useCallback, useMemo } from 'react'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { DocumentProspectingPageType } from '../../../models/DocumentProspecting'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  resetDocumentProspecting,
  selectDocumentProspectingBaseFormValues,
  selectDocumentProspectingBaseFormValuesLoading,
  setDocumentProspectingPage,
} from '../../../store/processDocument/documentProspecting'
import {
  resetProcessDocumentInfo,
  selectProcessDocumentFileInfo,
  selectProcessDocumentSelectedFileName,
  selectProcessDocumentUploadIsLoading,
} from '../../../store/processDocument/documentInfo'

import { ProcessDocumentFileUploadProgress, ProgressItem } from '../ProcessDocumentFileUploadProgress/ProcessDocumentFileUploadProgress'
import { ProcessDocumentFileInput } from '../ProcessDocumentFileInput/ProcessDocumentFileInput'
import { useDocumentUpload, useSafeTimeout } from '../hooks'

import { useProspectingBaseForm } from './hooks'

export const DocumentProspectingFileUpload = () => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const selectedFileName = useAppSelector(selectProcessDocumentSelectedFileName)

  const { uploadFile } = useDocumentUpload()
  const { generateFormValues } = useProspectingBaseForm()
  const { setSafeTimeout, clearSafeTimeout } = useSafeTimeout()

  const resetFileUpload = useCallback(() => {
    dispatch(resetProcessDocumentInfo())
    dispatch(resetDocumentProspecting())
  }, [dispatch])

  const _handleFileSelected = useCallback(
    async (file: File) => {
      clearSafeTimeout()

      const fileInfo = await uploadFile(file)
      if (!fileInfo) {
        resetFileUpload()
        return
      }

      const { fileId } = fileInfo

      const baseFormValues = await generateFormValues(fileId)
      if (!baseFormValues) {
        resetFileUpload()
        return
      }

      setSafeTimeout(() => dispatch(setDocumentProspectingPage(DocumentProspectingPageType.PROSPECTING_STEPS)), 500)
    },
    [clearSafeTimeout, dispatch, generateFormValues, resetFileUpload, setSafeTimeout, uploadFile],
  )

  const handleFileSelected = tracked(_handleFileSelected, { eventName: TrackEvent.DOCUMENT_PROSPECTING_FILE_ADDED })

  return (
    <div className='h-100 w-100 d-flex align-items-center justify-content-center'>
      <div className='d-flex flex-column align-items-center zocks-gap-2 document-prospecting-file-upload-container'>
        <div className='d-flex flex-column align-items-center zocks-gap-1 document-prospecting-file-upload-inner-container'>
          <div className='d-flex flex-column align-items-center zocks-gap-0.5'>
            <div className='py-1 zocks-px-0.75 zocks-bg-color-gray-100 border zocks-border-color-gray-200 document-prospecting-file-upload-label-wrapper'>
              <span className='lead-extra-small zocks-text-gray-700'>Document Intelligence</span>
            </div>
            <h3>Turn Document into Contact</h3>
          </div>
          <span className='paragraph-medium text-center'>
            Your AI Assistant extracts contact details from the file and prepares them for review before saving to Zocks and your CRM.
          </span>
        </div>
        {!selectedFileName ? <ProcessDocumentFileInput onFileSelected={handleFileSelected} /> : <DocumentProspectingFileUploadProgress />}
      </div>
    </div>
  )
}

const DocumentProspectingFileUploadProgress = () => {
  const selectedFileName = useAppSelector(selectProcessDocumentSelectedFileName)
  const isUploading = useAppSelector(selectProcessDocumentUploadIsLoading)
  const fileInfo = useAppSelector(selectProcessDocumentFileInfo)

  const baseFormValuesLoading = useAppSelector(selectDocumentProspectingBaseFormValuesLoading)
  const baseFormValues = useAppSelector(selectDocumentProspectingBaseFormValues)

  const progressItems: ProgressItem[] = useMemo(() => {
    if (!selectedFileName) {
      return []
    }
    return [
      {
        isLoading: isUploading,
        isSuccess: !isUploading && !!fileInfo,
        label: fileInfo ? `Uploaded ${selectedFileName}` : `Uploading ${selectedFileName}…`,
      },
      {
        isLoading: baseFormValuesLoading,
        isSuccess: !baseFormValuesLoading && !!baseFormValues,
        label: baseFormValues ? 'Contact details ready to review' : 'Extracting contact details…',
      },
    ]
  }, [baseFormValues, baseFormValuesLoading, fileInfo, isUploading, selectedFileName])

  return <ProcessDocumentFileUploadProgress items={progressItems} />
}
